import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "@ndd/ui";
import type { RdScheduleActionRequestBody } from "../../../../../shared/rd-schedule-actions";
import { authenticatedApi } from "../../../lib/api";
import {
  actionDoneMessage,
  isWaitTimeout,
  retryAndWait,
  scheduleOperation,
  submitAndWait,
  waitForAction,
  type ActionProblem,
  type ActionProgress,
  type ActionResume,
  type ScheduleApi,
  type ScheduleOperation,
} from "../../../lib/rd-schedule-actions";
import {
  captureScheduleFailure,
  captureScheduleWaitTimeout,
} from "./capture-schedule-failure";

// NO-876. The two bindings every action dialog needs: an authenticated
// schedule API with a stable identity, and one submit that stays pending
// until Healthie settles the action.

/**
 * The authenticated API, reporting a request that fails in the browser once
 * per operation until that operation succeeds again, so a poll loop on a bad
 * connection is one report, not sixty.
 */
export function useScheduleApi(): ScheduleApi {
  const { getToken } = useAuth();
  const tokenRef = useRef(getToken);
  const reported = useRef(new Set<ScheduleOperation>());
  tokenRef.current = getToken;

  return useCallback(async <T>(path: string, init?: RequestInit): Promise<T> => {
    const operation = scheduleOperation(path, init?.method);

    try {
      const response = await authenticatedApi<T>(tokenRef.current, path, init);
      reported.current.delete(operation);

      return response;
    } catch (error) {
      if (!reported.current.has(operation)) {
        reported.current.add(operation);
        captureScheduleFailure(operation, error);
      }

      throw error;
    }
  }, []);
}

export interface ActionSubmit {
  pending: boolean;
  problem: ActionProblem | null;
  /**
   * An earlier confirm saved the change but did not finish it. The next
   * confirm continues that same action, so the form stays as it was.
   */
  locked: boolean;
  /** Part of the change went through and the rest cannot continue: close and check the week. */
  stale: boolean;
  /** What the confirm button spreads: the wait, its real state and whether confirming retries. */
  confirmState: {
    pending: boolean;
    stale: boolean;
    progress: ActionProgress;
    retrying: boolean;
  };
  submit: (body: RdScheduleActionRequestBody) => Promise<void>;
  clearProblem: () => void;
}

export function useActionSubmit(input: {
  api: ScheduleApi;
  rdId: string | null;
  /** The RD's zone, for the time a success toast names. */
  timeZone: string;
  onSucceeded: () => void;
  /** A slot was taken while the dialog was open: reload its times. */
  onSlotConflict?: () => void;
  /** A saved change did not finish, possibly part applied: refresh the week behind the dialog. */
  onPartial?: () => void;
}): ActionSubmit {
  const { api, rdId, timeZone, onSucceeded, onSlotConflict, onPartial } = input;
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<ActionProblem | null>(null);
  const [resume, setResume] = useState<ActionResume | null>(null);
  const [stale, setStale] = useState(false);
  const [progress, setProgress] = useState<ActionProgress>("saving");
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = useCallback(
    async (body: RdScheduleActionRequestBody) => {
      setPending(true);
      setProgress("saving");
      setProblem(null);

      const result = await continueOrSubmit(api, rdId, body, resume, setProgress);

      if (!mounted.current) return;

      setPending(false);

      if (result.ok) {
        toast.success(actionDoneMessage(body, timeZone));
        onSucceeded();

        return;
      }

      setProblem(result.problem);
      setResume(result.resume);

      if (result.partial) setStale(true);

      if (result.partial || result.resume) onPartial?.();

      if (isWaitTimeout(result.problem)) captureScheduleWaitTimeout();

      if (result.problem.slotConflict) onSlotConflict?.();
    },
    [api, rdId, timeZone, resume, onSucceeded, onSlotConflict, onPartial]
  );

  const clearProblem = useCallback(() => setProblem(null), []);

  return {
    pending,
    problem,
    locked: resume !== null || stale,
    stale,
    confirmState: { pending, stale, progress, retrying: resume?.mode === "retry" },
    submit,
    clearProblem,
  };
}

function continueOrSubmit(
  api: ScheduleApi,
  rdId: string | null,
  body: RdScheduleActionRequestBody,
  resume: ActionResume | null,
  onProgress: (progress: ActionProgress) => void
) {
  if (resume?.mode === "retry") {
    return retryAndWait(api, rdId, resume.actionId, { onProgress });
  }

  if (resume?.mode === "poll") {
    return waitForAction(api, rdId, resume.actionId, {
      escalateToRetry: true,
      onProgress,
    });
  }

  return submitAndWait(api, rdId, body, { onProgress });
}
